import React from 'react';
import { common, query } from '@kurtosys/ksys-app-template';
import { IComponentStyle } from '@kurtosys/types/componentStyle/IComponentStyle.js';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { factHasValue } from '../../../shared/factHasValue.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import AccordionPanel from '../../base/AccordionPanel/AccordionPanel.js';
import List from '../../base/List/List.js';
import Fact from '../Fact/Fact.js';
import { IFactProps } from '../Fact/models/index.js';

import { IFactListConfiguration, IFactListProps, IFactListState } from './models/index.js';
import Wrapper from './styledComponents/Wrapper.js';
import { FACT_LIST_CATEGORY_KEY, FACT_LIST_COMPONENT_KEY } from './FactList.meta.js';

export class FactList extends ComponentBase<IFactListProps, IFactListState, IFactListConfiguration> {
	categoryKey = FACT_LIST_CATEGORY_KEY;
	componentKey = FACT_LIST_COMPONENT_KEY;

	constructor(props: IFactListProps) {
		super(props);

		makeObservable(this, {
			itemBlankDataOptions: computed,
			listBlankDataOptions: computed,
			items: computed,
			factList: computed,
			factAccordion: computed,
		});
	}

	get itemBlankDataOptions() {
		const { itemBlankDataOptions = {} } = this.props;
		const defaults = {
			showBlank: true,
			placeholder: 'N/A',
		};
		return Object.assign(defaults, itemBlankDataOptions);
	}
	get listBlankDataOptions() {
		const { listBlankDataOptions = {} } = this.props;
		const defaults = {
			showBlank: true,
			placeholder: 'No list data available',
		};
		return Object.assign(defaults, listBlankDataOptions);
	}

	showItem(item: IFactProps): boolean {
		const { showBlank } = this.itemBlankDataOptions;
		const { blankDataOptions } = item;
		const itemShowBlank =
			blankDataOptions && !common.commonUtils.isNullOrUndefined(blankDataOptions.showBlank)
				? blankDataOptions.showBlank
				: showBlank;

		if (item.hide) {
			return false;
		}

		return itemShowBlank || factHasValue(item);
	}

	get items() {
		const { items = [], listValueSeparator, onDocumentClick, conditionalStyles } = this.props;

		const mappedItems = items
			.filter((item) => this.showItem(item))
			.map((factProps: IFactProps) => {
				const blankDataOptions = { ...this.itemBlankDataOptions, ...factProps.blankDataOptions };
				if (!factProps.onDocumentClick && onDocumentClick) {
					factProps.onDocumentClick = onDocumentClick;
				}

				let componentStyle: IComponentStyle = {};
				if (!common.commonUtils.isNullOrEmpty(conditionalStyles)) {
					for (const conditionalStyle of conditionalStyles) {
						const conditionalHelper = new common.helpers.ConditionalHelper(
							{ queryClass: query.Query },
							conditionalStyle.conditional,
						);
						if (conditionalHelper.matches(factProps.payload || {})) {
							componentStyle = { ...componentStyle, ...conditionalStyle.style };
						}
					}
				}

				return (
					<Fact
						{...factProps}
						listValueSeparator={factProps.listValueSeparator || listValueSeparator}
						blankDataOptions={blankDataOptions}
						componentStyle={componentStyle}
					/>
				);
			});
		return mappedItems;
	}

	get factList() {
		const { heading, headingTooltip } = this.props;
		const items = this.items;
		return (
			<List
				items={items}
				heading={heading}
				headingTooltip={headingTooltip}
				blankDataOptions={this.listBlankDataOptions}
			/>
		);
	}

	get factAccordion() {
		const { heading = '', headingTooltip } = this.props;
		const items = this.items;
		return (
			<AccordionPanel heading={heading} headingTooltip={headingTooltip} panelKey={heading} isOpen={true}>
				<List items={items} blankDataOptions={this.listBlankDataOptions} />
			</AccordionPanel>
		);
	}

	render() {
		const { className, mode = 'list' } = this.props;
		const { showBlank } = this.listBlankDataOptions;
		const items = this.items;

		if (items.length === 0 && !showBlank) {
			return null;
		}

		return <Wrapper className={className}>{mode === 'list' ? this.factList : this.factAccordion}</Wrapper>;
	}
}

export default InjectedStyledComponent(observer(FactList), FACT_LIST_CATEGORY_KEY, FACT_LIST_COMPONENT_KEY);
